/**
 * Unified stop parser: identity is the work order (daily) or the event (master), else the route slot.
 * `mapStopToListItemFormat` / `mapMasterStopToListItemFormat` are backward-compatible aliases.
 */
import { get } from 'lodash'

import { getLatitude, getLongitude } from 'data/address/addressSelectors'
import { combineAddressInfo } from 'utils/helper'

import { deriveRecurrence, parseDaysOfWeek } from './recurrence'

export const STOP_MODE = {
  DAILY: 'daily',
  MASTER: 'master',
}

/**
 * Parses a raw API stop object into a UI-ready shape.
 *
 * @param {Object} stop - raw stop from API response
 * @param {string} [mode=STOP_MODE.DAILY] - 'daily' or 'master'
 * @returns {Object} UI-ready stop shape
 */
export const parseStop = (stop, mode = STOP_MODE.DAILY) => {
  const isMaster = mode === STOP_MODE.MASTER

  // Mirrors GeoJsonUtils.featureId — a routable stop has no work order or event, so its slot is the only id its marker shares.
  const workOrderId = get(stop, 'workOrderId')
  const id = (isMaster ? get(stop, 'configuredServiceEventId') : workOrderId) || get(stop, 'routeSlotId')

  // Shared fields
  const addressStr = combineAddressInfo(stop)
  const accountName = get(stop, 'accountName')
  const addressName = get(stop, 'addressName', '')
  const latitude = getLatitude(stop)
  const longitude = getLongitude(stop)
  const sequence = get(stop, 'sequence')
  const position = get(stop, 'position')
  const configuredServiceEventId = get(stop, 'configuredServiceEventId')
  const serviceName = get(stop, 'serviceName')
  const pricedServiceType = get(stop, 'pricedServiceType', '')
  const recurrenceFrequency = get(stop, 'recurrenceFrequency', 1)
  const recurrencePer = get(stop, 'recurrencePer', '')
  const recurrenceInterval = get(stop, 'recurrenceInterval', '')
  const configuredServiceEventType = get(stop, 'configuredServiceEventType', '')

  const { pricingPeriod, recurrence, showWeekSummary } = deriveRecurrence({
    recurrencePer,
    recurrenceInterval,
    recurrenceFrequency,
    pricedServiceType,
    configuredServiceEventType,
  })

  const daysOfWeek = get(stop, 'daysOfWeek', '')
  const parsedDaysOfWeek = parseDaysOfWeek(daysOfWeek, pricingPeriod)

  const base = {
    id,
    configuredServiceEventId,
    configuredServiceEventType,
    addressStr,
    addressName,
    accountName,
    latitude,
    longitude,
    sequence,
    position,
    serviceName,
    pricedServiceType,
    recurrence,
    showWeekSummary,
    pricingPeriod,
    daysOfWeek,
    parsedDaysOfWeek,
    recurrenceFrequency,
  }

  if (isMaster) {
    return { ...base, routeId: get(stop, 'routeId') }
  }

  return {
    ...base,
    workOrderId,
    workOrderStatus: get(stop, 'workOrderStatus'),
  }
}

/**
 * Backward-compatible alias: parses a daily stop.
 * @param {Object} stop
 * @returns {Object}
 */
export const mapStopToListItemFormat = stop => parseStop(stop, STOP_MODE.DAILY)

/**
 * Backward-compatible alias: parses a master stop.
 * @param {Object} stop
 * @returns {Object}
 */
export const mapMasterStopToListItemFormat = stop => parseStop(stop, STOP_MODE.MASTER)
